import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from './auth/auth.service';
import { AfterViewInit, Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { AlertController, ModalController, NavController, ToastController } from '@ionic/angular';
import { PushNotifications, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { environment } from '../environments/environment';
import { UserService } from '../services/user.service';
import { NotificationService } from './notification.service';
import { FirebaseMessagingService } from './firebase-messaging.service';
import { NetworkConnectionService } from './services/network-connection.service';
import { ApiService } from '../services/api.service';
import { AuthStateService, Show } from './auth/auth-state.service';
import { TranslateService } from '@ngx-translate/core';
import { SentryConfig, SentryService } from '../services/sentry.service';
import { SelectShowModalComponent } from './select-show-modal/select-show-modal.component';
import { Privileges } from '../models/privileges';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
    public pushNotificationToken: string = '';

    public sideMinWidth = '250px';
    public isCollapsed = false;

    public menuItems: {
        route: string;
        label: string;
        icon: string;
        matchPrefix: boolean;
        isCustomIcon: boolean;
        requiredPrivilege: string;
        isDivider: boolean;
        /**
         * When set, this item is only visible if `activeShow[requiresShowFlag]`
         * is true. Same `keyof Show` indirection that `ShowFlagGuard` uses on
         * routes, so menu and route guard share one pattern. Adding a new
         * per-show feature flag is now: add the field to Show, set
         * `requiresShowFlag` here, set `data: { showFlag }` on the route.
         */
        requiresShowFlag?: keyof Show;
    }[] = [
        {
            route: '/dashboard',
            label: this.translateService.instant('menu.dashboard'),
            icon: 'grid-outline',
            matchPrefix: false,
            isCustomIcon: false, // Geen aangepast icoon
            requiredPrivilege: null,
            isDivider: false
        },
        {
            route: null,
            label: 'Onderhoud',
            icon: null,
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: true
        },
        {
            route: '/tasks',
            label: 'Klussenlijst',
            icon: 'hammer-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasTasks'
        },
        {
            route: '/maintenance',
            label: 'Periodiek Onderhoud',
            icon: '/assets/icon/operations-icon.svg',
            matchPrefix: false,
            isCustomIcon: true, // Aangepast icoon
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasMaintenance'
        },
        {
            route: null,
            label: 'Agenda',
            icon: null,
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: true
        },
        {
            route: '/performances',
            label: 'Speeldata',
            icon: 'calendar-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasPerformances'
        },
        {
            route: '/planner',
            label: 'Toneelmeester Planning',
            icon: 'calendar-number-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: Privileges.PlannerView,
            isDivider: false,
            requiresShowFlag: 'hasPlanner'
        },
        {
            route: null,
            label: 'Rapportage',
            icon: null,
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: true
        },
        {
            route: '/logboek',
            label: 'Toneelmeester Logboek',
            icon: 'book-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasLog'
        },
        {
            route: '/showreports',
            label: 'Showreports',
            icon: 'clipboard-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasShowreports'
        },
        {
            route: '/magicaluna/performer-fly',
            label: 'Performer Fly Inspectie',
            icon: 'shield-checkmark-outline',
            matchPrefix: true,
            isCustomIcon: false,
            // Every user linked to a show with hasPerformerFlyInspections
            // gets the menu item. CREATE + DELETE are still gated by
            // their respective privileges inside the page.
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasPerformerFlyInspections'
        },
        {
            route: null,
            label: 'Voorstelling',
            icon: null,
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: true
        },
        {
            route: '/opstartprocedure',
            label: 'Opstartprocedure',
            icon: 'receipt-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasStartupProcedure'
        },

        {
            route: '/showtimer',
            label: 'Timer',
            icon: 'time-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasShowTimer'
        },

        {
            route: '/protocols',
            label: 'Documentatie',
            icon: 'briefcase-outline',
            matchPrefix: false,
            isCustomIcon: false,
            // TODO: 'protocols_read' isn't defined in the DB's privilege table
            // — item stays hidden for everyone. Decide whether to add the
            // privilege or drop this gate.
            requiredPrivilege: 'protocols_read',
            isDivider: false,
            requiresShowFlag: 'hasProtocols'
        },
        {
            route: '/changementen',
            label: 'Changementenlijst',
            icon: 'git-compare',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasSceneryChangeList'
        },
        {
            route: '/showstop',
            label: 'Showstopprocedure',
            icon: 'sad-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasShowStopProcedure'
        },
        {
            route: '/ontruiming',
            label: 'Ontruimingsinstructies',
            icon: '/assets/icon/emergency-exit-icon.svg',
            matchPrefix: false,
            isCustomIcon: true, // Aangepast icoon
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasEvacuationInstructions'
        },
        {
            route: '/contacts',
            label: 'Contactpersonen',
            icon: 'call-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false,
            requiresShowFlag: 'hasContactList'
        },
        {
            route: null,
            label: 'Beheer',
            icon: null,
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: true
        },
        {
            route: '/admin/users',
            label: 'Gebruikersbeheer',
            icon: 'people-outline',
            matchPrefix: true,
            isCustomIcon: false,
            // Menu visibility aligned with the route guard on /admin/users,
            // which checks 'users_admin'. Previously this was 'users_edit'
            // which caused the menu item to hide even when the user could
            // access the page via users_admin.
            requiredPrivilege: Privileges.UsersAdmin,
            isDivider: false
        },
        {
            route: '/admin/shows',
            label: 'Productiebeheer',
            icon: 'film-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: Privileges.ShowsAdmin,
            isDivider: false
        },
        {
            route: '/admin/privileges',
            label: 'Rechtenbeheer',
            icon: 'shield-checkmark-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: Privileges.PrivilegesAdmin,
            isDivider: false
        },
        {
            route: '/admin/templates',
            label: 'Sjabloonbeheer',
            icon: 'document-text-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: Privileges.TemplateView,
            isDivider: false
        },
        {
            route: '/admin/email-tester',
            label: 'E-mail tester',
            icon: 'mail-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: Privileges.EmailTestAdmin,
            isDivider: false
        },
        {
            route: '/admin/notification-tester',
            label: 'Notificatie tester',
            icon: 'notifications-outline',
            matchPrefix: true,
            isCustomIcon: false,
            requiredPrivilege: Privileges.EmailTestAdmin,
            isDivider: false
        },
        {
            route: '/rehearsal/admin',
            label: 'Repetitie Instellingen',
            icon: 'accessibility-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: Privileges.SceneAdmin,
            isDivider: false
        },
        {
            route: null,
            label: 'App',
            icon: null,
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: true
        },
        {
            route: '/settings',
            label: 'Instellingen',
            icon: 'settings-outline',
            matchPrefix: false,
            isCustomIcon: false,
            requiredPrivilege: null,
            isDivider: false
        }

    ];

    // {
    //
    //     route: '/rehearsal/add',
    //     label: 'Repetities',
    //     icon: 'accessibility-outline',
    //     matchPrefix: false,
    //     isCustomIcon: false,
    //     requiredPrivilege: null,
    //     isDivider: false
    // },

    public currentRoute: string = '/dashboard';

    public isAppUser = true;
    public isAuthenticated = false;
    public isOnNoShowPage = false;
    public isOnStandalonePage = false;

    public isPushNotificationsAvailable =
        Capacitor.isPluginAvailable('PushNotifications');

    /** A notification-tap deep link held until auth is ready, then navigated to. */
    private pendingDeepLinkUrl: string | null = null;
    /** True once authState.initialize() has completed. */
    private authReady = false;
    /** Guards against attaching the tap listener more than once. */
    private notificationListenerRegistered = false;

    /** Map of divider indices to their visibility status */
    public sectionVisibility: Map<number, boolean> = new Map();

    protected authState = inject(AuthStateService);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Drives visibility of the side-menu show-switcher button. Bound to the
     * `hasMultipleShows` computed signal on AuthStateService, so when an
     * admin links the user to a new show during a session — and the
     * subsequent refresh fires (visibility change, opening the modal) —
     * the button appears without any manual change-detection plumbing.
     */
    public readonly shouldShowShowSwitcher = this.authState.hasMultipleShows;

    /**
     * Reactively recomputes section visibility whenever the signed-in user
     * changes. Replaces the old manual subscription-to-userService flow,
     * which had a race window where `getActiveUser()` was called before
     * `authState.user` was populated, leaving privilege-gated menu items
     * (e.g. admin pages) hidden until the next navigation.
     */
    private readonly sectionVisibilityEffect = effect(() => {
        // Track both signals so visibility recomputes when the user changes
        // (privilege gating) or when the active show changes (hasLog gating).
        this.authState.user();
        this.authState.activeShow();
        this.updateSectionVisibility();
    });

    private clickCount: number = 0;
    private clickTimer: any;

    private isSelectShowModalOpen = false;

    constructor(
        private authService: AuthService,
        private router: Router,
        private navController: NavController,
        private alertController: AlertController,
        private toastController: ToastController,
        private modalController: ModalController,
        private userService: UserService,
        private notificationService: NotificationService,
        private readonly firebaseMessaging: FirebaseMessagingService,
        private readonly networkConnectionService: NetworkConnectionService,
        private readonly translateService: TranslateService,
        private readonly sentryService: SentryService,
        private readonly apiService: ApiService
    ) {
    }

    public async ngOnInit(): Promise<void> {

        // Attach the notification-tap handler BEFORE the awaits below, so a
        // cold-start tap (app launched from a killed state by tapping a
        // notification) is captured rather than lost while the session restores.
        await this.registerNotificationDeepLinkListener();

        await this.networkConnectionService.initialize();
        await this.authState.initialize();

        // Auth is ready now — safe to follow a pending deep link past the guards.
        this.authReady = true;
        this.consumePendingDeepLink();

        this.setupShowsRefreshOnFocus();

        // Subscribe to authservice isAuthenticated
        this.authService.isAuthenticated.subscribe(isAuthenticated => {
            this.isAuthenticated = isAuthenticated;
            // A tap that arrived before login can now be followed (e.g. the app
            // was killed and the session had to be restored / the user logged in).
            if (isAuthenticated) {
                this.consumePendingDeepLink();
            }
        });

        if (environment.enableSentry) {
            await this.initializeSentry();
        }
        if (this.isPushNotificationsAvailable) {

            await this.firebaseMessaging.init();

            this.notificationService.isPushNotificationsAvailable = true;
            this.setupPushNotifications();
        } else {
            console.log('Push notifications are not available');
            this.notificationService.isPushNotificationsAvailable = false;
        }

        // Luister naar routewijzigingen
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.currentRoute = event.urlAfterRedirects;
                this.isOnNoShowPage = this.currentRoute.startsWith('/no-show');
                this.isOnStandalonePage = this.currentRoute.startsWith('/account-deletion');

                // Check if user needs to select a show after navigation
                // (but not on auth or no-show pages)
                if (!this.currentRoute.startsWith('/auth') && !this.currentRoute.startsWith('/no-show')) {
                    this.checkAndShowSelectShowModal();
                }
            }
        });
    }

    public async ngAfterViewInit() {
        // User is now loaded in ngOnInit via the subscription
        // This hook can be used for view-dependent initialization if needed
    }

    /**
     * Initializes Sentry error tracking with the current environment configuration.
     * Sets user context after initialization for better error attribution.
     */
    private async initializeSentry(): Promise<void> {
        const config: SentryConfig = {
            dsn: environment.sentryDsn,
            release: 'carologboekreader@' + environment.appVersion,
            dist: environment.build,
            environment: environment.appEnvironment,
            enabled: environment.enableSentry
        };

        this.sentryService.initialize(config);

        // Set user context for error attribution
        const activeUserId = await this.userService.getActiveUserId();
        let activeUserUsername: string | null = null;

        if (activeUserId != null) {
            const activeUser = await this.userService.getUser(activeUserId);
            activeUserUsername = activeUser?.username ?? null;
        }

        this.sentryService.setUserContext(activeUserId, activeUserUsername);
    }

    public async logOut() {
        await this.authService.logout();
        await this.navController.navigateRoot('/auth');
    }

    public getAppVersion(): string {
        if (environment.production !== true) {
            return environment.appEnvironment + '-' + environment.appVersion;
        }
        return environment.appVersion;
    }

    public getAppName(): string {
        return environment.appName ?? 'Showlogger';
    }

    public async onLogout() {
        const alert = await this.alertController.create({
            header: 'Uitloggen?',
            message: 'Weet je zeker dat je uit wilt loggen?',
            buttons: [
                {
                    text: 'Nee'
                },
                {
                    text: 'Ja',
                    handler: () => this.logOut()
                }
            ]
        });

        await alert.present();
    }

    public async showDeviceInfo(): Promise<void> {
        this.clickCount++;

        if (this.clickCount === 1) {
            this.clickTimer = setTimeout(() => {
                this.clickCount = 0;
            }, 5000);
        }

        if (this.clickCount === 5) {
            clearTimeout(this.clickTimer);
            this.clickCount = 0;
            // await this.displayDeviceInfoModal();
            await this.displayUserInformation();
        }
    }

    public setSideMenuCollapsed(b: boolean): void {
        this.isCollapsed = b;
    }

    /**
     * Returns the Ionic color attribute for the menu toolbar.
     * Only used when no custom hex color is set.
     */
    public get menuToolbarColor(): string | null {
        return this.authState.activeShowPrimaryColor ? null : 'primary';
    }

    /**
     * Returns custom styles for the menu toolbar when using a hex color.
     */
    public get menuToolbarStyle(): { [key: string]: string } | null {
        const hexColor = this.authState.activeShowPrimaryColor;
        if (!hexColor) {
            return null;
        }
        return {
            '--background': hexColor,
            '--color': '#ffffff'
        };
    }

    /**
     * Returns the style for active menu items when using a custom show color.
     */
    public getActiveMenuItemStyle(isActive: boolean): { [key: string]: string } | null {
        if (!isActive) {
            return null;
        }
        const hexColor = this.authState.activeShowPrimaryColor;
        if (!hexColor) {
            return null;
        }
        return {
            '--background': hexColor,
            '--color': '#ffffff'
        };
    }

    /**
     * Returns the Ionic color for active menu items.
     * Returns 'primary' only when no custom hex color is set.
     */
    public getActiveMenuItemColor(isActive: boolean): string {
        if (!isActive) {
            return '';
        }
        return this.authState.activeShowPrimaryColor ? '' : 'primary';
    }

    /**
     * Check if a menu section (starting at divider index) has any visible items.
     * A section includes all items after the divider until the next divider.
     */
    public hasSectionVisibleItems(dividerIndex: number): boolean {
        // Look at items after this divider until the next divider
        for (let i = dividerIndex + 1; i < this.menuItems.length; i++) {
            const item = this.menuItems[i];

            // Stop at the next divider
            if (item.isDivider) {
                break;
            }

            // Check if this item is visible — privilege satisfied AND, if the
            // item is gated on a show flag, that flag is on.
            const privilegeOk = !item.requiredPrivilege || this.authState.user()?.hasPrivilege(item.requiredPrivilege);
            const showFlagOk = !item.requiresShowFlag
                || this.authState.activeShow()?.[item.requiresShowFlag] === true;
            if (privilegeOk && showFlagOk) {
                return true;
            }
        }
        return false;
    }

    /**
     * Precompute visibility for all menu sections.
     * Invoked reactively via sectionVisibilityEffect whenever the user changes.
     */
    private updateSectionVisibility(): void {
        this.sectionVisibility.clear();
        this.menuItems.forEach((item, index) => {
            if (item.isDivider) {
                this.sectionVisibility.set(index, this.hasSectionVisibleItems(index));
            }
        });
    }

    /**
     * Checks if the user has multiple shows but no active show selected.
     * If so, shows a modal to let the user select an active show.
     */
    public async checkAndShowSelectShowModal(): Promise<void> {
        const user = this.authState.user();
        const activeShow = this.authState.activeShow();
        const shows = this.authState.availableShows();

        // Only show modal if:
        // 1. User exists
        // 2. User has multiple shows (more than 1)
        // 3. No active show is currently selected
        // 4. Modal is not already open
        if (user && shows.length > 1 && !activeShow && !this.isSelectShowModalOpen) {
            console.log('[AppComponent] User has multiple shows but no active show - showing modal');
            await this.showSelectShowModal(shows, { dismissible: false });
        }
    }

    /**
     * Opens the show-picker modal so a multi-show user can voluntarily
     * switch their active show from the side menu. Refreshes the shows
     * list before opening so newly-linked shows appear without a
     * logout/login cycle. Falls back to the cached list on network
     * failure so the switcher still works offline.
     */
    public async openShowSwitcher(): Promise<void> {
        if (this.isSelectShowModalOpen) {
            return;
        }

        try {
            await this.apiService.refreshAvailableShows();
        } catch (error) {
            console.warn('[AppComponent] refreshAvailableShows failed; using cached list', error);
        }

        const shows = this.authState.availableShows();
        if (shows.length < 2) {
            return;
        }
        await this.showSelectShowModal(shows, { dismissible: true });
    }

    /**
     * Shows the modal for selecting an active show.
     *
     * @param shows       The list of shows the user can pick from.
     * @param options.dismissible  When false (first-time flow) the user must
     *                             pick a show — tapping the backdrop is blocked.
     *                             When true (voluntary switcher) the user can
     *                             cancel by dismissing the modal.
     */
    private async showSelectShowModal(
        shows: Show[],
        options: { dismissible: boolean }
    ): Promise<void> {
        this.isSelectShowModalOpen = true;

        const modal = await this.modalController.create({
            component: SelectShowModalComponent,
            componentProps: {
                shows: shows
            },
            backdropDismiss: options.dismissible,
            cssClass: 'select-show-modal'
        });

        await modal.present();

        const { data, role } = await modal.onWillDismiss();

        this.isSelectShowModalOpen = false;

        if (role === 'confirm' && data) {
            console.log('[AppComponent] Show selected:', data);
            // Refresh the page to apply the new show context
            window.location.reload();
        }
    }

    /**
     * Re-fetch the user's shows whenever the tab/app regains focus. This
     * keeps the side-menu show-switcher button visibility (and the modal
     * contents) in sync with the backend after an admin links or unlinks
     * a show — without polling, and without forcing a logout/login. Best-
     * effort: errors are logged and swallowed so a hiccup never disrupts
     * the rest of the app. Cleanup is wired through DestroyRef.
     */
    private setupShowsRefreshOnFocus(): void {
        if (typeof document === 'undefined') {
            return;
        }
        const handler = (): void => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            if (!this.authState.isAuthenticated) {
                return;
            }
            this.apiService.refreshAvailableShows().catch(error =>
                console.warn('[AppComponent] refreshAvailableShows on focus failed', error)
            );
        };
        document.addEventListener('visibilitychange', handler);
        this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', handler));
    }

    private setupPushNotifications(): void {
        this.checkNotificationPermissions();
        // Tap handling is registered early in ngOnInit (registerNotificationDeepLinkListener).
        this.initPushNotificationListenerToast();
    }

    private initPushNotificationListenerToast() {
        // Show us the notification payload if the app is open on our device
        PushNotifications.addListener(
            'pushNotificationReceived',
            async (notification: PushNotificationSchema) => {
                // alert('Push received: ' + JSON.stringify(notification));
                const toast = await this.toastController.create({
                    message: notification.title,
                    duration: 5000,
                    position: 'bottom',
                    layout: 'stacked'
                    // buttons: [
                    //   {
                    //     text: 'Toon',
                    //     role: 'info',
                    //     handler: () => {
                    //       alert('Navigeer naar bericht');
                    //     },
                    //   },
                    // ],
                });

                // await toast.present();
            }
        );
    }

    private async displayDeviceInfoModal() {
        let deviceToken = this.notificationService.deviceToken;

        if (!deviceToken || deviceToken.trim() === '') {
            deviceToken = 'Geen token beschikbaar';
        }

        const alert = await this.alertController.create({
            header: 'Apparaat informatie',
            message: 'Token<br><br><textarea>'                + deviceToken                + '</textarea><br>Notifications possible on this device: '                + this.isPushNotificationsAvailable                + '<br>Notifications permissions granted: '                + this.notificationService.permissionsGranted                + '<br>Registration error: '                + this.notificationService.registrationError,
            buttons: ['OK']
        });

        await alert.present();
    }

    /**
     * Attaches the notification-tap handler once, as early as possible. On tap we
     * resolve the target route and stash it; the actual navigation is deferred to
     * {@link consumePendingDeepLink} so it only runs once auth is ready — otherwise
     * the route guards (AuthGuard/NoShowGuard/ShowFlagGuard) bounce a cold-start
     * deep link straight to the dashboard.
     */
    private async registerNotificationDeepLinkListener(): Promise<void> {
        if (!this.isPushNotificationsAvailable || this.notificationListenerRegistered) {
            return;
        }
        this.notificationListenerRegistered = true;
        await PushNotifications.addListener('pushNotificationActionPerformed', action => {
            const url = this.deepLinkUrlForNotificationData(action?.notification?.data);
            if (!url) {
                return;
            }
            this.pendingDeepLinkUrl = url;
            this.consumePendingDeepLink();
        });
    }

    /** Maps a notification data payload ({ type, id }) to an in-app route, or null. */
    private deepLinkUrlForNotificationData(data: any): string | null {
        if (!data || data.id == null) {
            return null;
        }
        switch (data.type) {
            case 'showreport':
                return `/showreports/${ data.id }`;
            case 'log':
                return `/logboek/${ data.id }`;
            case 'task':
                return `/tasks/${ data.id }`;
            default:
                return null;
        }
    }

    /** Navigate to a stashed deep link, but only once auth is ready and the user is signed in. */
    private consumePendingDeepLink(): void {
        if (!this.pendingDeepLinkUrl || !this.authReady || !this.authState.isAuthenticated) {
            return;
        }
        const url = this.pendingDeepLinkUrl;
        this.pendingDeepLinkUrl = null;
        this.navController.navigateRoot(url);
    }

    private async checkNotificationPermissions() {
        PushNotifications.requestPermissions().then(result => {
            if (result.receive === 'granted') {
                // Register with Apple / Google to receive push via APNS/FCM
                this.notificationService.permissionsGranted = true;

                PushNotifications.register();
            } else {
                // Show some error
                this.notificationService.permissionsGranted = false;
            }
        });

        // PushNotifications.addListener(
        //     'registration',
        //     async (token: any) => {
        //         this.pushNotificationToken = JSON.stringify(token.value);
        //         await this.notificationService.registerDevice(token.value);
        //     }
        // );

        PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Error during registration: ', JSON.stringify(error));
            this.notificationService.registrationError = JSON.stringify(error);
        });
    }

    private async displayUserInformation() {

        let messageToDisplay = 'test';
        const user = this.authState.user();

        if (!user) {
            messageToDisplay = 'Gebruiker kon niet opgehaald worden';
        } else {
            messageToDisplay = `<b>Gebruiker: </b> ${ user.getFullName() }<br>`;
            messageToDisplay += `<b>Id: </b> ${ user.getId() }<br>`;
            messageToDisplay += '<br>';
            if (user.privilages) {
                messageToDisplay += '<b>Permissies</b><br>';
                messageToDisplay += '<ul>';
                user.privilages?.forEach(p => messageToDisplay += `<li>${ p.name }</li>`);
                messageToDisplay += '</ul>';
            } else {
                messageToDisplay += 'Geen permissies gevonden';

            }
        }

        const alert = await this.alertController.create({
            header: 'Gebruikersinformatie',
            message: messageToDisplay,
            buttons: ['Sluiten']
        });

        await alert.present();
    }
}
